/**
 * Lightweight client for the /api/usage/* endpoints.
 *
 * Usage:
 *   const { usage, refresh, increment } = useDailyUsage();
 *   if (!usage?.is_premium && usage?.samurai.remaining === 0) { ... }
 *
 *   try { await increment("samurai"); }
 *   catch (e) { if (e.response?.status === 429) showPaywall(); }
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useDailyUsage() {
  const { getToken, isAuthenticated } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [getToken]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setUsage(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await axios.get(`${API}/usage/me`, { headers: headers() });
      setUsage(res.data);
      return res.data;
    } catch {
      setUsage(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, headers]);

  const increment = useCallback(async (feature) => {
    const res = await axios.post(
      `${API}/usage/increment`,
      { feature },
      { headers: headers() },
    );
    // Update local state without a full refetch
    setUsage((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [feature]: {
          used: res.data.used,
          limit: res.data.limit ?? null,
          remaining: res.data.remaining ?? null,
        },
      };
    });
    return res.data;
  }, [headers]);

  useEffect(() => { refresh(); }, [refresh]);

  return { usage, loading, refresh, increment };
}
